import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MdArrowBack, MdDownload, MdListAlt, MdLocalShipping, MdShoppingBag, MdSync } from 'react-icons/md';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import logger from '../utils/logger';

const MOBILE_BREAKPOINT = 700;


function OrderConfirmation() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get orderId from URL params or query string
  const orderIdToFetch = orderId || searchParams.get('orderId');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    if (!orderIdToFetch) {
      // Try to get from sessionStorage (set during checkout)
      const storedOrderId = sessionStorage.getItem('pendingOrderId');
      if (storedOrderId) {
        navigate(`/order-confirmation/${storedOrderId}`, { replace: true });
      } else {
        setError('Order ID not found');
        setLoading(false);
      }
      return;
    }

    fetchOrderDetails(orderIdToFetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIdToFetch, user]);

  const normalizeOrderData = (rawOrder) => {
    if (!rawOrder) return null;

    const normalizedItems = (rawOrder.items || rawOrder.Items || []).map((item) => ({
      ProductName: item.productName || item.ProductName || 'Product',
      Quantity: Number(item.quantity ?? item.Quantity ?? 0),
      PriceAtPurchase: Number(item.priceAtPurchase ?? item.PriceAtPurchase ?? 0),
      LineTotal: Number(item.lineTotal ?? item.LineTotal ?? 0)
    }));

    return {
      OrderId: rawOrder.orderId ?? rawOrder.OrderId,
      OrderNumber: rawOrder.orderNumber ?? rawOrder.OrderNumber,
      CreatedAt: rawOrder.createdAt ?? rawOrder.CreatedAt,
      OrderStatus: rawOrder.orderStatus ?? rawOrder.OrderStatus,
      PaymentStatus: rawOrder.paymentStatus ?? rawOrder.PaymentStatus,
      TotalAmount: Number(rawOrder.totalAmount ?? rawOrder.TotalAmount ?? 0),
      ShippingCharge: Number(rawOrder.shippingCharge ?? rawOrder.ShippingCharge ?? 0),
      DiscountAmount: Number(rawOrder.discountAmount ?? rawOrder.DiscountAmount ?? 0),
      Address: {
        FullName: rawOrder.address?.fullName ?? rawOrder.Address?.FullName ?? '',
        PhoneNumber: rawOrder.address?.phoneNumber ?? rawOrder.Address?.PhoneNumber ?? '',
        AddressLine1: rawOrder.address?.addressLine1 ?? rawOrder.Address?.AddressLine1 ?? '',
        AddressLine2: rawOrder.address?.addressLine2 ?? rawOrder.Address?.AddressLine2 ?? '',
        City: rawOrder.address?.city ?? rawOrder.Address?.City ?? '',
        State: rawOrder.address?.state ?? rawOrder.Address?.State ?? '',
        PostalCode: rawOrder.address?.postalCode ?? rawOrder.Address?.PostalCode ?? '',
        Country: rawOrder.address?.country ?? rawOrder.Address?.Country ?? ''
      },
      Items: normalizedItems
    };
  };

  const fetchOrderDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);

      logger.log('📦 Fetching order details', { orderId: id });

      const res = await api.get(`/aastrog_cart/orders/${id}`);

      if (res.data.success) {
        const normalizedOrder = normalizeOrderData(res.data.data);
        setOrder(normalizedOrder);
        logger.log('✅ Order details fetched', normalizedOrder);

        // Clear pending order from sessionStorage
        sessionStorage.removeItem('pendingOrderId');
        sessionStorage.removeItem('pendingOrderNumber');
      } else {
        setError(res.data.error || 'Failed to load order details');
      }
    } catch (err) {
      logger.error('❌ fetchOrderDetails error', err);
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': '#FFA500',
      'Confirmed': '#4CAF50',
      'Processing': '#2196F3',
      'Shipped': '#9C27B0',
      'Delivered': '#4CAF50',
      'Cancelled': '#F44336',
      'Failed': '#F44336'
    };
    return statusColors[status] || '#999';
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      'Pending': '#FFA500',
      'Completed': '#4CAF50',
      'Failed': '#F44336',
      'Refunded': '#2196F3'
    };
    return statusColors[status] || '#999';
  };

  const downloadInvoice = async () => {
    try {
      setDownloadingInvoice(true);
      logger.log('📄 Downloading invoice', { orderId: orderIdToFetch });

      // TODO: Implement invoice download API
      // For now, show message
      alert('Invoice download feature coming soon!');

      // Future implementation:
      // const res = await api.get(`/aastrog_cart/orders/${orderIdToFetch}/invoice`, {
      //   responseType: 'blob'
      // });
      // const url = window.URL.createObjectURL(new Blob([res.data]));
      // const link = document.createElement('a');
      // link.href = url;
      // link.setAttribute('download', `Invoice_${order?.OrderNumber}.pdf`);
      // document.body.appendChild(link);
      // link.click();
      // link.remove();
    } catch (err) {
      logger.error('❌ downloadInvoice error', err);
      alert('Failed to download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading-message">
          <h3>⏳ Loading order details...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cart-container">
        <div className="error-message">
          <h3>⚠️ {error}</h3>
          <button className="continue-shopping-btn" onClick={() => navigate('/products')}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="cart-container">
        <div className="empty-cart">
          <h3>Order not found</h3>
          <button className="continue-shopping-btn" onClick={() => navigate('/products')}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container order-confirmation-page">
      <div className="cart-header">
        <button className="back-button product-details-back-button" onClick={() => navigate('/products')} aria-label="Go back">
          <MdArrowBack size={isMobile ? 16 : 24} />
          {!isMobile && 'Back'}
        </button>
        <h1 className="cart-title">Order Confirmation</h1>
      </div>

      {/* Success Banner */}
      <div className="order-success-banner" style={{
        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        padding: isMobile ? '14px 12px' : '18px 20px',
        borderRadius: isMobile ? '10px' : '12px',
        textAlign: 'center',
        marginBottom: isMobile ? '16px' : '20px',
        color: 'white',
        maxWidth: '900px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div style={{ fontSize: isMobile ? '28px' : '34px', marginBottom: isMobile ? '4px' : '6px', lineHeight: 1 }}>✅</div>
        <h2 style={{ margin: '0 0 6px 0', fontSize: isMobile ? '18px' : '22px', lineHeight: 1.25 }}>Order Placed Successfully!</h2>
        <p style={{ margin: 0, fontSize: isMobile ? '12px' : '14px', lineHeight: 1.4, opacity: 0.9 }}>
          Thank you for your order. We'll send you a confirmation email shortly.
        </p>
      </div>

      {/* Order Details Card */}
      <div className="order-details-card" style={{
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '25px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ marginTop: 0, color: '#FFD700', fontSize: '20px' }}>Order Information</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px' }}>
          <div>
            <div style={{ color: '#999', fontSize: '13px', marginBottom: '5px' }}>Order Number</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#FFD700' }}>{order.OrderNumber}</div>
          </div>

          <div>
            <div style={{ color: '#999', fontSize: '13px', marginBottom: '5px' }}>Order Date</div>
            <div style={{ fontSize: '14px' }}>{formatDate(order.CreatedAt)}</div>
          </div>

          <div>
            <div style={{ color: '#999', fontSize: '13px', marginBottom: '5px' }}>Order Status</div>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: getStatusColor(order.OrderStatus),
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '20px',
              background: `${getStatusColor(order.OrderStatus)}22`
            }}>
              {order.OrderStatus}
            </div>
          </div>

          <div>
            <div style={{ color: '#999', fontSize: '13px', marginBottom: '5px' }}>Payment Status</div>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: getPaymentStatusColor(order.PaymentStatus),
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '20px',
              background: `${getPaymentStatusColor(order.PaymentStatus)}22`
            }}>
              {order.PaymentStatus}
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Address */}
      {order.Address && (
        <div className="delivery-address-card" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '25px',
          borderRadius: '12px',
          marginBottom: '25px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ marginTop: 0, color: '#FFD700', fontSize: '20px' }}>Delivery Address</h3>
          <div style={{ lineHeight: '1.6', fontSize: '14px' }}>
            <strong>{order.Address.FullName}</strong><br />
            {order.Address.PhoneNumber}<br />
            {order.Address.AddressLine1}<br />
            {order.Address.AddressLine2 && <>{order.Address.AddressLine2}<br /></>}
            {order.Address.City}, {order.Address.State} - {order.Address.PostalCode}<br />
            {order.Address.Country}
          </div>
        </div>
      )}

      {/* Order Items */}
      <div className="order-items-card" style={{
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '25px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ marginTop: 0, color: '#FFD700', fontSize: '20px' }}>Order Items</h3>
        
        <div className="order-items-list">
          {order.Items && order.Items.length > 0 ? (
            order.Items.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px 0',
                borderBottom: index < order.Items.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>
                    {item.ProductName}
                  </div>
                  <div style={{ fontSize: '13px', color: '#999' }}>
                    Quantity: {item.Quantity} × ₹{item.PriceAtPurchase?.toFixed(2)}
                  </div>
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#FFD700' }}>
                  ₹{item.LineTotal?.toFixed(2)}
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: '#999', padding: '20px 0', textAlign: 'center' }}>
              No items found
            </div>
          )}
        </div>
      </div>

      {/* Order Summary */}
      <div className="order-summary-card" style={{
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '25px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ marginTop: 0, color: '#FFD700', fontSize: '20px' }}>Order Summary</h3>
        
        <div style={{ marginTop: '20px' }}>
          <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px' }}>
            <span>Subtotal</span>
            <span>₹{(order.TotalAmount - (order.ShippingCharge || 0) + (order.DiscountAmount || 0)).toFixed(2)}</span>
          </div>

          {order.DiscountAmount > 0 && (
            <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px', color: '#4CAF50' }}>
              <span>Discount</span>
              <span>- ₹{order.DiscountAmount?.toFixed(2)}</span>
            </div>
          )}

          <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px' }}>
            <span>Shipping Charge</span>
            <span>{order.ShippingCharge > 0 ? `₹${order.ShippingCharge.toFixed(2)}` : 'FREE'}</span>
          </div>

          <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '15px 0' }}></div>

          <div className="summary-row total" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '18px', fontWeight: 'bold' }}>
            <span>Total Amount</span>
            <span style={{ color: '#FFD700' }}>₹{order.TotalAmount?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '12px',
        marginTop: '24px',
        maxWidth: '760px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <button 
          className="continue-btn"
          onClick={downloadInvoice}
          disabled={downloadingInvoice}
          aria-label="Download Invoice"
          title="Download Invoice"
          style={{ width: '100%', margin: 0, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
        >
          {downloadingInvoice ? <MdSync size={24} /> : <MdDownload size={24} />}
          {!isMobile && (downloadingInvoice ? 'Downloading...' : 'Download Invoice')}
        </button>

        <button 
          className="continue-btn"
          onClick={() => navigate('/orders')}
          aria-label="View All Orders"
          title="View All Orders"
          style={{ width: '100%', margin: 0, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
        >
          <MdListAlt size={24} />
          {!isMobile && 'View All Orders'}
        </button>

        <button 
          className="continue-btn"
          onClick={() => navigate(`/orders/${order.OrderId}/track`)}
          aria-label="Order Tracking"
          title="Order Tracking"
          style={{ width: '100%', margin: 0, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
        >
          <MdLocalShipping size={24} />
          {!isMobile && 'Order Tracking'}
        </button>

        <button 
          className="continue-btn"
          onClick={() => navigate('/products')}
          aria-label="Continue Shopping"
          title="Continue Shopping"
          style={{ width: '100%', margin: 0, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
        >
          <MdShoppingBag size={24} />
          {!isMobile && 'Continue Shopping'}
        </button>
      </div>

      {/* Order Tracking Info */}
      {order.OrderStatus !== 'Delivered' && order.OrderStatus !== 'Cancelled' && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: 'rgba(33, 150, 243, 0.1)',
          border: '1px solid rgba(33, 150, 243, 0.3)',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            📦 Your order is being processed. You'll receive tracking information via email once shipped.
          </p>
        </div>
      )}
    </div>
  );
}

export default OrderConfirmation;
